import { LanguagesEnum } from '@/api/enums';
import {
  AccountAdminTask,
  AdminTaskStatus,
  AdminTaskTagEnum,
} from '@/client/admin-tasks';
import { LanguageContext } from '@/common/context/LanguageContext';
import { AppButton } from '@/ui/buttons';
import { AppChip } from '@/ui/chip';
import { FlexContainer } from '@/ui/styled-ui';
import { getAdminTaskDetails } from '@/utils/helpers';
import { t } from 'i18next';
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox';
import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const StyledTaskCard = styled(FlexContainer)`
  overflow: hidden;
  width: 100%;
  background: var(--beige-main);
  border: 1px solid var(--gray-dark);
  border-radius: var(--medium-border-radius);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  padding: var(--medium-padding) var(--default-padding);
`;

const StyledTaskHeader = styled.div`
  height: 100%;
  background: var(--beige-main);
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: var(--default-padding);
`;

const StyledTaskInfo = styled(FlexContainer)`
  height: 100%;
  flex: 1;
  align-items: center;
  justify-content: flex-start;
  gap: var(--default-padding);
`;

const StyledTaskTitle = styled.div`
  margin: 0;
  font-size: var(--medium-font-size);
  font-weight: 600;
`;

const StyledTaskCheckbox = styled(Checkbox)`
  width: 30px;
  min-width: 30px;
  height: 30px;

  .p-checkbox-box {
    width: 30px;
    min-width: 30px;
    height: 30px;
    align-items: center !important;
    border: 1px solid var(--gray-dark);
    border-radius: 50%;
    background: var(--white-main);

    &.p-highlight,
    &:hover,
    &.p-highlight:hover,
    &:focus {
      border-color: var(--green-dark) !important;
      background: var(--green-pale) !important;
    }

    .p-checkbox-icon {
      width: 12px;
      height: 12px;
      font-size: 12px;
      color: var(--green-dark);
    }
  }

  &:not(.p-checkbox-disabled) .p-checkbox-box:hover {
    border-color: var(--red-main);
  }

  &:not(.p-checkbox-disabled) .p-checkbox-box.p-focus {
    box-shadow: var(--box-shadow-red);
    border-color: var(--red-main);
  }

  &:not(.p-checkbox-disabled) .p-checkbox-box.p-highlight:hover {
    border-color: var(--red-main);
    background: var(--red-main);
  }
`;

const StyledChip = styled(AppChip)`
  .p-chip-text {
    min-width: max-content;
  }
`;

const StyledTaskActions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--small-padding);
`;

const StyledTaskDescription = styled.div<{ expanded: boolean }>`
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  align-self: flex-start;
  max-height: ${({ expanded }) => (expanded ? '200px' : '0')};
  opacity: ${({ expanded }) => (expanded ? '1' : '0')};
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  padding-top: ${({ expanded }) => (expanded ? 'var(--small-padding)' : '0')};
`;

const StyledTasksActionButton = styled(AppButton)<{ $maxWidth: number }>`
  &.tasks-action-button {
    min-width: ${({ $maxWidth }) => $maxWidth}px;
    justify-content: center;
  }
`;

type TaskCardProps = {
  adminTask: AccountAdminTask;
  expanded: boolean;
  onTaskClick: (taskId: string) => void;
  onTaskAction: (
    task: AccountAdminTask,
    action: AdminTaskStatus,
  ) => Promise<void>;
  style?: React.CSSProperties;
  maxButtonWidth: number;
  id?: string;
};

export const TaskCard: React.FC<TaskCardProps> = ({
  adminTask,
  expanded,
  onTaskClick,
  onTaskAction,
  style,
  maxButtonWidth,
  id,
}) => {
  const navigate = useNavigate();
  const { language } = useContext(LanguageContext);
  const { name, label, tagKey, buttonLabel, description, redirectUrl, isLink } =
    getAdminTaskDetails(adminTask, language as LanguagesEnum);
  const [isChecked, setIsChecked] = useState(
    adminTask.status === AdminTaskStatus.COMPLETED,
  );

  const isTodo = adminTask.status === AdminTaskStatus.TODO;
  const currentButtonLabel = isTodo ? buttonLabel : t('generic.restore');

  return (
    <StyledTaskCard
      id={id}
      key={adminTask.id}
      style={style}
      onClick={() => description && onTaskClick(adminTask?.id || '')}
    >
      <StyledTaskHeader>
        <StyledTaskInfo>
          <StyledTaskCheckbox
            inputId={`task-checkbox-${adminTask.task.key}`}
            checked={isChecked}
            onClick={(e) => e.stopPropagation()}
            onChange={(e: CheckboxChangeEvent) => {
              if (!isTodo) {
                return;
              }

              setIsChecked(!!e.checked);
              onTaskAction(
                adminTask,
                e.checked ? AdminTaskStatus.COMPLETED : AdminTaskStatus.TODO,
              );
            }}
          />
          <FlexContainer direction="column" gap={0} style={{ flex: 1 }}>
            <FlexContainer justify="flex-start" gap={16}>
              <StyledTaskTitle id={`task-title-${adminTask.task.key}`}>
                {name}
              </StyledTaskTitle>
              {label && (
                <StyledChip
                  size="s"
                  label={label}
                  type={
                    tagKey === AdminTaskTagEnum.REQUIRED_GO_LIVE
                      ? 'purple'
                      : 'purple-outline'
                  }
                />
              )}
            </FlexContainer>

            {description && (
              <StyledTaskDescription
                expanded={expanded}
                id={`task-description-${adminTask.task.key}`}
              >
                {description}
              </StyledTaskDescription>
            )}
          </FlexContainer>
        </StyledTaskInfo>

        <StyledTaskActions onClick={(e) => e.stopPropagation()}>
          {isLink && adminTask.status === AdminTaskStatus.TODO ? (
            <a href={redirectUrl} target="_blank" rel="noopener noreferrer">
              <StyledTasksActionButton
                id={`task-link-button-${adminTask.task.key}`}
                className="tasks-action-button"
                label={buttonLabel}
                severity="success"
                $maxWidth={maxButtonWidth}
              />
            </a>
          ) : (
            <StyledTasksActionButton
              id={
                isTodo
                  ? `task-button-${adminTask.task.key}`
                  : `task-restore-button-${adminTask.task.key}`
              }
              className="tasks-action-button"
              label={currentButtonLabel}
              severity="success"
              onClick={(e: any) => {
                e.stopPropagation();
                isTodo
                  ? navigate(redirectUrl)
                  : onTaskAction(adminTask, AdminTaskStatus.TODO);
              }}
              $maxWidth={maxButtonWidth}
            />
          )}
          {isTodo && (
            <AppButton
              id={`task-skip-button-${adminTask.task.key}`}
              label={t('generic.skip')}
              severity="secondary"
              className="ml-3 p-0"
              type="text"
              size="sm"
              onClick={(e: any) => {
                e.stopPropagation();
                onTaskAction(adminTask, AdminTaskStatus.SKIPPED);
              }}
            />
          )}
        </StyledTaskActions>
      </StyledTaskHeader>
    </StyledTaskCard>
  );
};
